<template>
  <v-card
    v-if="
      !configGetter($rttI18n.tenantId, themeId).disable5StarRating &&
      trackingData.feedback
    "
    :class="
      feedbackChannel === 'QUESTIONPRO'
        ? 'nps'
        : feedbackChannel === 'TRUSTPILOT'
          ? 'trustpilot'
          : ''
    "
    class="pa-4 mb-6"
    variant="flat"
  >
    <div
      v-if="feedbackChannel === 'QUESTIONPRO'"
      class="feedback pa-0 mx-0 v-col-12"
    >
      <NPSComponent />
    </div>
    <template v-else-if="feedbackChannel === 'STAR_RATING'">
      <div v-if="!ratingData && !sent" class="feedback pa-0 mx-0 v-col-12">
        <h3 class="header font-weight-bold text-center pa-0 mb-3">
          {{ $t('feedback.header') }}
        </h3>
        <v-form ref="ratingForm" v-model="valid" :lazy-validation="lazy">
          <div
            :aria-label="$t('feedback.header')"
            class="text-center"
            role="radiogroup"
          >
            <button
              v-for="index in stars"
              :key="index"
              :aria-checked="this.rating === index"
              :aria-label="$t(`feedback.starsValue${index}`)"
              :style="getStyle(index)"
              class="star"
              role="radio"
              v-on:mouseout="hoverStars(0)"
              v-on:mouseover="hoverStars(index)"
              v-on:click.prevent="setRating(index)"
            />
          </div>
          <div v-if="rating" class="mt-4">
            <v-textarea
              v-model="message"
              :counter="messageLength"
              :label="$t('feedback.messageLabel')"
              :placeholder="$t('feedback.messagePlaceholder')"
              :rules="messageRules"
              bg-color="#fff"
              class="message"
              color="anchor"
              no-resize
              variant="outlined"
            ></v-textarea>
            <v-btn
              :block="true"
              :disabled="!valid"
              class="text-body-1"
              color="info"
              type="submit"
              variant="flat"
              v-on:click="addRatingWrapper"
            >
              {{ $t('generic.send') }}
            </v-btn>
          </div>
        </v-form>
      </div>
      <div
        v-else-if="ratingData && !sent"
        class="feedback text-center pa-4 mx-0 v-col-12"
      >
        <p class="header font-weight-bold pa-0">
          {{ $t('feedback.headerRated') }}
        </p>
        <div
          :aria-label="$t('feedback.headerRated')"
          class="text-center mb-4"
          role="radiogroup"
        >
          <button
            v-for="index in stars"
            :key="index"
            :aria-checked="this.rating === index"
            :aria-label="$t(`feedback.starsValue${index}`)"
            :style="getStyle(index)"
            class="star"
            role="radio"
          />
        </div>
        <div v-if="showMessage && ratingData.message">
          <p class="header font-weight-bold pa-0">
            {{ $t('feedback.headerRatedComment') }}
          </p>
          <p>
            {{ this.ratingData.message }}
          </p>
        </div>
      </div>
      <div v-else-if="sent" class="feedback text-center pa-2 mx-0 v-col-12">
        <p class="header font-weight-bold pa-0">
          {{ $t('feedback.headerSent') }}
        </p>
      </div>
    </template>
    <div
      v-else-if="feedbackChannel === 'TRUSTPILOT'"
      class="trustpilot-content"
    >
      <img
        :alt="$t('feedback.trustPilot.header')"
        class=""
        src="/trustpilot_image.png"
      />
      <h2>
        {{ $t('feedback.trustPilot.content') }}
      </h2>
      <p class="font-weight-bold">
        {{ $t('feedback.header') }}&nbsp;
        <a href="" v-on:click.prevent="openTrustPilotDialog">{{
          $t('feedback.trustPilot.button')
        }}</a>
      </p>
    </div>
  </v-card>
</template>

<script>
import { mapActions, mapState } from 'pinia';
import { configGetter } from '@/config/configGetter';
import publicPath from '@/util/publicPath';
import { getUtmSource } from '@/router/queryParam.js';
import NPSComponent from '@/components/NPSComponent.vue';
import { sendAnalytics } from '@/services/analytics.js';
import dialogs from '@/components/dialogs/dialogs.js';
import { useDialogStore } from '@/stores/dialog.js';
import { useThemeStore } from '@/stores/theme.js';
import { useTrackingDataStore } from '@/stores/trackingData.js';
import { useRatingStore } from '@/stores/rating.js';

export default {
  name: 'FeedbackComponent',
  components: {
    NPSComponent
  },
  props: {
    postcode: {
      type: String,
      default: null
    },
    ratingData: {
      type: Object,
      default: null
    },
    trackingId: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      hover: 0,
      lazy: false,
      message: this.ratingData?.message || '',
      messageLength: 256,
      messageRules: [
        v =>
          v.length <= this.messageLength ||
          this.$t('feedback.messageExceededLength')
      ],
      rating: this.ratingData?.stars || 0,
      sent: false,
      showMessage: !!this.ratingData?.message || false,
      stars: 5,
      valid: true,
      configGetter
    };
  },
  computed: {
    ...mapState(useTrackingDataStore, {
      trackingData: 'data',
      consigneeAddress: 'consigneeAddress',
      feedbackChannel: 'feedbackChannel'
    }),
    ...mapState(useThemeStore, ['themeId'])
  },
  methods: {
    ...mapActions(useDialogStore, ['toggleDialog']),
    ...mapActions(useRatingStore, ['addRating']),
    addRatingWrapper() {
      this.addRating({
        utmSource: getUtmSource(this.$route),
        tenantId: this.$rttI18n.tenantId,
        postcode: this.postcode,
        trackingId: this.trackingId,
        body: {
          message: this.message,
          stars: this.rating
        }
      });

      sendAnalytics(this.trackingData, 'clickStats', 'feedback_submitted', {
        rating: this.rating,
        comment: this.message
      });

      this.sent = true;
    },
    hoverStars(rating) {
      this.hover = rating;
    },
    setRating(rating) {
      if (this.rating === rating) {
        this.rating = 0;
        this.hover = 0;
      } else {
        this.rating = rating;
      }

      sendAnalytics(this.trackingData, 'clickStats', 'rating_star_clicked', {
        rating
      });
    },
    getStyle(index) {
      return `background-size: contain; background-image: url(${publicPath(
        `/themes/${this.themeId}/feedback/star-${
          this.hover >= index || this.rating >= index ? 'active' : 'inactive'
        }.svg`
      )})`;
    },
    openTrustPilotDialog() {
      sendAnalytics(this.trackingData, 'clicksStats', 'trustPilot', {
        text: this.$t('feedback.trustPilot.consent'),
        name: this.consigneeAddress?.name,
        fromFooter: false
      });
      this.toggleDialog(dialogs.TRUST_PILOT_CONSENT_DIALOG);
    }
  }
};
</script>

<style scoped>
.header {
  font-size: 14px;
}

.feedback .message fieldset {
  border: 0.5px solid rgba(28, 35, 49, 0.3);
  border-radius: 0;
}

.feedback .star {
  display: inline-block;
  width: 30px;
  height: 30px;
}

.nps {
  background-color: #d8e5ef;
}

.trustpilot {
  background: #cce7bc;
}

.trustpilot-content {
  margin-left: 1em;
  margin-right: 1em;
}

.trustpilot h2 {
  margin-bottom: 0.5em;
}

.trustpilot-content img {
  float: right;
  height: 120px;
  margin-left: 1em;
}

.trustpilot-content p {
  font-size: 14px;
}
</style>
